import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@features/auth/hooks/useAuth';
import { useAuthStore } from '@features/auth/store/authStore';
import { cn } from '@/styles/tailwind-utils';

// ─── Icons ────────────────────────────────────────────────────────────────────

const svgBase = {
  fill: 'none' as const,
  stroke: 'currentColor' as const,
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function LogoIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
      <rect x="2" y="2" width="13" height="13" rx="3" fill="white" />
      <rect x="21" y="2" width="13" height="13" rx="3" fill="white" />
      <rect x="2" y="21" width="13" height="13" rx="3" fill="white" />
      <rect x="21" y="21" width="13" height="13" rx="3" fill="white" opacity="0.4" />
    </svg>
  );
}

function OverviewIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" {...svgBase}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function CalendarNavIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" {...svgBase}>
      <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" {...svgBase}>
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function ServicesIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" {...svgBase}>
      <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

function StaffIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" {...svgBase}>
      <path d="M12 12a5 5 0 100-10 5 5 0 000 10zM3 21a9 9 0 0118 0" />
    </svg>
  );
}

function InvoiceIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" {...svgBase}>
      <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function ReportsIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" {...svgBase}>
      <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

function SettingsNavIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" {...svgBase}>
      <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" {...svgBase}>
      <path d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" {...svgBase}>
      <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  );
}

function HamburgerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

// ─── Nav item definitions ─────────────────────────────────────────────────────

const NAV_ITEM_DEFS = [
  { key: 'dashboard',  href: '/account/dashboard',     icon: <OverviewIcon /> },
  { key: 'calendar',   href: '/account/calendar',      icon: <CalendarNavIcon /> },
  { key: 'clients',    href: '/account/clients',       icon: <UsersIcon /> },
  { key: 'specialties', href: '/account/specialties',  icon: <ServicesIcon /> },
  { key: 'staff',      href: '/account/staff',          icon: <StaffIcon /> },
  { key: 'invoices',   href: '/account/invoices',      icon: <InvoiceIcon /> },
  { key: 'reports',    href: '/account/reports',       icon: <ReportsIcon /> },
  { key: 'settings',   href: '/account/settings',      icon: <SettingsNavIcon /> },
] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isNavItemActive(href: string, pathname: string) {
  return pathname.startsWith(href);
}

// ─── User dropdown (desktop) ─────────────────────────────────────────────────

interface UserMenuProps {
  fullName: string;
  initials: string;
  labelSettings: string;
  labelLogout: string;
  onLogout: () => void;
}

function UserMenu({ fullName, initials, labelSettings, labelLogout, onLogout }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, []);

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => { setOpen((prev) => !prev); }}
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-white/90 transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-semibold text-white">
          {initials}
        </div>
        {/* Name + chevron only at lg+ to avoid crowding the tablet nav */}
        <span className="hidden lg:inline">{fullName}</span>
        <span className="hidden lg:flex"><ChevronDownIcon /></span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1.5 w-44 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5">
          <Link
            to="/account/settings"
            onClick={() => { setOpen(false); }}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-muted"
          >
            <SettingsNavIcon />
            {labelSettings}
          </Link>
          <div className="border-t border-border" />
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-destructive transition-colors hover:bg-error-subtle"
          >
            <LogoutIcon />
            {labelLogout}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Mobile / tablet nav drawer ───────────────────────────────────────────────

interface MobileNavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

function MobileNavDrawer({ isOpen, onClose }: MobileNavDrawerProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const { logout } = useAuth();

  const fullName = user ? `${user.firstName} ${user.lastName}` : '';
  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : '';

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 md:hidden',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-primary shadow-xl transition-transform duration-200 ease-in-out md:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        role="dialog"
        aria-modal="true"
        aria-label={t('layout.mainNavigation')}
      >
        {/* Drawer header */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 px-4">
          <Link
            to="/account"
            onClick={onClose}
            className="flex items-center gap-2.5 rounded focus:outline-none focus:ring-2 focus:ring-white/40"
          >
            <LogoIcon />
            <span className="font-display text-xl font-semibold tracking-wide text-white">
              Calendo
            </span>
          </Link>
          <button
            onClick={onClose}
            aria-label={t('layout.closeDialog')}
            className="flex h-9 w-9 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Nav links */}
        <nav aria-label={t('layout.mainNavigation')} className="flex-1 overflow-y-auto px-3 py-4">
          {NAV_ITEM_DEFS.map((item) => {
            const isActive = isNavItemActive(item.href, pathname);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onClose}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'mb-0.5 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white',
                )}
              >
                <span className="shrink-0">{item.icon}</span>
                {t(`layout.account.nav.${item.key}`)}
              </Link>
            );
          })}
        </nav>

        {/* User card + actions pinned to the bottom */}
        {user && (
          <div className="shrink-0 border-t border-white/10 p-4">
            <div className="mb-3 flex items-center gap-3 rounded-md px-2 py-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-semibold text-white">
                {initials}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-white">{fullName}</div>
                <div className="truncate text-xs text-white/50">{user.email}</div>
              </div>
            </div>

            <Link
              to="/account/settings"
              onClick={onClose}
              className="mb-0.5 flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              <SettingsNavIcon />
              {t('layout.account.userMenu.settings')}
            </Link>

            <button
              onClick={() => { onClose(); logout(); }}
              className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-red-300"
            >
              <LogoutIcon />
              {t('layout.account.userMenu.logout')}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────

interface AccountHeaderProps {
  onMobileNavOpen: () => void;
}

function AccountHeader({ onMobileNavOpen }: AccountHeaderProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const { logout } = useAuth();

  const fullName = user ? `${user.firstName} ${user.lastName}` : '';
  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : '';

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-2 bg-primary px-4 shadow-md lg:gap-4 lg:px-6">
      {/* Logo */}
      <Link
        to="/account"
        className="flex shrink-0 items-center gap-2.5 text-white focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-1 focus:ring-offset-primary"
      >
        <LogoIcon />
        <span className="font-display text-xl font-semibold tracking-wide text-white">
          Calendo
        </span>
      </Link>

      {/* Desktop + tablet nav — icon-only on md, icon+text on lg+ */}
      <nav
        className="hidden md:flex flex-1 items-center justify-center gap-0.5"
        aria-label={t('layout.mainNavigation')}
      >
        {NAV_ITEM_DEFS.map((item) => {
          const isActive = isNavItemActive(item.href, pathname);
          return (
            <Link
              key={item.href}
              to={item.href}
              aria-current={isActive ? 'page' : undefined}
              title={t(`layout.account.nav.${item.key}`)}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white/40 lg:px-3',
                isActive
                  ? 'bg-white/20 text-white'
                  : 'text-white/75 hover:bg-white/10 hover:text-white',
              )}
            >
              {item.icon}
              {/* Text label hidden on md, visible on lg+ */}
              <span className="hidden lg:inline">
                {t(`layout.account.nav.${item.key}`)}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* Spacer — pushes right-side items to far right on mobile */}
      <div className="flex-1 md:hidden" />

      {/* User menu — visible on md+ (desktop dropdown) */}
      {user && (
        <div className="hidden md:block">
          <UserMenu
            fullName={fullName}
            initials={initials}
            labelSettings={t('layout.account.userMenu.settings')}
            labelLogout={t('layout.account.userMenu.logout')}
            onLogout={() => { logout(); }}
          />
        </div>
      )}

      {/* Hamburger — only on mobile (below md) */}
      <button
        className="flex h-9 w-9 items-center justify-center rounded-md text-white/80 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40 md:hidden"
        onClick={onMobileNavOpen}
        aria-label={t('layout.toggleMenu')}
      >
        <HamburgerIcon />
      </button>
    </header>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────

interface AccountLayoutProps {
  children: React.ReactNode;
}

export function AccountLayout({ children }: AccountLayoutProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <AccountHeader onMobileNavOpen={() => setMobileNavOpen(true)} />
      <MobileNavDrawer isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <main>{children}</main>
    </div>
  );
}

// ─── Shared coming-soon section ───────────────────────────────────────────────

interface ComingSoonSectionProps {
  title: string;
}

export function ComingSoonSection({ title }: ComingSoonSectionProps) {
  const { t } = useTranslation();

  return (
    <section className="w-full">
      <div className="flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center gap-3 px-4 text-center">
        <h1 className="font-display text-3xl font-semibold text-foreground md:text-4xl">{title}</h1>
        <p className="text-base text-muted-foreground">{t('layout.account.comingSoon')}</p>
      </div>
    </section>
  );
}
